"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import { calcRoofing, type RoofingResult, type RoofingMaterial, type RoofingPitch, type RoofingWork } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const MATERIAL_OPTIONS = [
  { value: "asphalt",  label: "Asphalt Shingles (3-tab or architectural) — Standard" },
  { value: "wood",     label: "Wood Shake Shingles — Mid-range" },
  { value: "metal",    label: "Metal Roofing — Premium" },
  { value: "premium",  label: "Tile / Slate — High-end" },
];
const PITCH_OPTIONS = [
  { value: "low",    label: "Low Pitch — 2/12 to 4/12 (nearly flat)" },
  { value: "medium", label: "Medium Pitch — 4/12 to 7/12 (standard)" },
  { value: "steep",  label: "Steep Pitch — 7/12+ (special equipment)" },
];
const MODE_OPTIONS = [
  { value: "tearoff",  label: "Full tear-off (remove existing shingles — recommended)" },
  { value: "overlay",  label: "Overlay / re-roof over existing layer" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [footprintSqft, setFootprintSqft] = useState("");
  const [material, setMaterial] = useState<RoofingMaterial>("asphalt");
  const [pitch, setPitch] = useState<RoofingPitch>("medium");
  const [mode, setMode] = useState<RoofingWork>("tearoff");
  const [numExtras, setNumExtras] = useState("0");
  const [result, setResult] = useState<RoofingResult | null>(null);

  function handleCalculate() {
    setResult(calcRoofing({
      footprintSqFt: parseFloat(footprintSqft) || 0,
      material,
      pitch,
      workType: mode,
      numExtras: parseInt(numExtras) || 0,
    }));
  }

  const spec = CALCULATOR_SPECS.roofing;

  return (
    <CalculatorShell
      name="Roofing Cost Calculator"
      description="Estimate the full cost to replace your roof — from budget asphalt shingles to premium metal or slate, with tear-off included."
      icon="roofing"
      category="high-value"
      accentColor="#f97316"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Roofing costs vary by region, local labor markets, and material availability. Always get 3+ quotes from licensed, insured contractors."
      inputsPanel={
        <div className="space-y-4">
          <InputField id="footprint-sqft" label="House Footprint" unit="sq ft" type="number" min="0" step="50" placeholder="1500" value={footprintSqft} onChange={(e) => { setFootprintSqft(e.target.value); setResult(null); }} hint="Measure the floor plan area — pitch factor is applied automatically" />
          <SelectField id="roofing-material" label="Roofing Material" value={material} onChange={(e) => { setMaterial(e.target.value as RoofingMaterial); setResult(null); }} options={MATERIAL_OPTIONS} />
          <SelectField id="roof-pitch" label="Roof Pitch" value={pitch} onChange={(e) => { setPitch(e.target.value as RoofingPitch); setResult(null); }} options={PITCH_OPTIONS} hint="Steeper roofs require more material and specialized labor" />
          <SelectField id="roofing-mode" label="Removal Method" value={mode} onChange={(e) => { setMode(e.target.value as RoofingWork); setResult(null); }} options={MODE_OPTIONS} hint="IRC limits asphalt roofs to 2 total layers" />
          <InputField id="num-extras" label="Extras (chimneys + skylights)" type="number" min="0" step="1" placeholder="0" value={numExtras} onChange={(e) => { setNumExtras(e.target.value); setResult(null); }} hint="Each chimney or skylight adds ~$500–$800 in flashing labor" />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Roof Squares" value={result.roofSquares} unit="squares" accent sub="1 square = 100 sq ft" />
              <ResultCard label="Actual Roof Area" value={result.roofSqFt.toLocaleString()} unit="sq ft" accent />
            </div>
            <div className="py-5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--ink-faint)" }}>Total Cost Range (Installed)</p>
              <p className="text-[2.6rem] leading-none font-black tracking-tight mb-2" style={{ color: "var(--ink)" }}>{fmt(result.totalMin)} – {fmt(result.totalMax)}</p>
              <p className="text-[11px] font-mono" style={{ color: "var(--ink-faint)" }}>midpoint {fmt(result.totalMid)} · ±20% regional variance</p>
            </div>
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Cost Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: `Decking + materials (${result.roofSquares} squares)`, value: fmt(result.baseCost) },
                  ...(result.extrasCost > 0 ? [{ label: "Extras (chimneys / skylights)", value: fmt(result.extrasCost) }] : []),
                  { label: "Total midpoint", value: fmt(result.totalMid), bold: true },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                      row.bold
                        ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30"
                        : "border-l-3 border-l-transparent"
                    }`}
                  >
                    <span className={`text-[12.5px] font-sans ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                      {row.label}
                    </span>
                    <span className={`font-mono text-[13px] ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Roofing Material Selection Guide</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            The roofing market offers a wide range of materials with very different <Link href="/articles/roof-lifespan-by-material/" className="text-[var(--accent)] hover:underline font-semibold">material lifespans</Link>, maintenance requirements, and price points. Architectural asphalt shingles are by far the most popular choice, with manufacturer warranties ranging from <strong>{spec.buyingGuide.asphaltShingleLifespan.value}</strong> depending on grade. For homeowners seeking a lifetime roof solution, metal standing seam systems last <strong>{spec.buyingGuide.metalRoofLifespan.value}</strong> with minimal maintenance and superior wind and hail resistance. All roofing materials in the US are priced by the <strong>&quot;square&quot; — {spec.buyingGuide.roofingSquare.value}</strong>. Material orders include a 10–15% overage for hips, valleys, ridge caps, and waste at cuts.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/roofing_buying_guide.webp" alt="Close-up of architectural asphalt shingles being installed on a residential roof" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/roofing_site_prep.webp" alt="Roofers performing tear-off of old shingles exposing the roof decking for inspection" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Tear-Off, Underlayment &amp; Code Compliance</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Under the International Residential Code, a maximum of <strong>{spec.sitePrep.maxLayersAllowed.value} layers</strong> of asphalt shingles are allowed. A third layer is prohibited and requires a complete tear-off before new material can be installed. While overlaying saves roughly 25% on labor, it adds 1,500–3,000 lbs to the structure and prevents inspection of the decking for <Link href="/articles/10-signs-you-need-a-new-roof/" className="text-[var(--accent)] hover:underline font-semibold">rot, moisture damage, or structural issues</Link>. Every new roof must use a minimum <strong>{spec.sitePrep.underlaymentRequirement.value}</strong> underlayment under IRC Section R905.2.3. In cold climates, self-adhering polymer-modified bitumen ice-and-water shield is additionally required for the first 24 inches from the eave line.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.windRatingStandard.standardCode}
        sourceName={spec.industryStandards.windRatingStandard.sourceName}
        sourceUrl={spec.industryStandards.windRatingStandard.sourceUrl}
        paragraphs={[
          <p key="p1">
            Not all roofing shingles are rated equally for wind resistance. ASTM D3161 Class F shingles are tested to withstand <b>110 MPH winds</b> — the minimum required in ENERGY STAR certified products and most standard installation areas. In hurricane-prone coastal regions and wind-borne debris areas, local building codes may require <b>UL 2390 Class H shingles — rated up to {spec.industryStandards.windRatingStandard.value}</b>. This higher rating requires specific nailing patterns (6 nails per shingle instead of 4) and closer fastener spacing at eaves and rakes.
          </p>,
          <p key="p2">
            Homeowners in Florida, the Gulf Coast, and coastal Carolinas should always verify that their chosen shingle carries both a Class A fire rating and the appropriate wind uplift classification for their county. Many insurers now require proof of wind-rated roofing materials before issuing or renewing homeowners insurance in high-risk wind corridors. Additionally, the Florida Building Code requires Miami-Dade County Notice of Acceptance (NOA) approval for all roofing materials used in the High-Velocity Hurricane Zone.
          </p>
        ]}
        keyRequirements={[
          "ASTM D3161 Class F shingles must withstand wind speeds of 110 MPH.",
          "UL 2390 Class H shingles are rated for wind speeds up to 150 MPH.",
          "Fastening in high-wind regions requires 6 nails per shingle.",
          "All roofing shingles must carry a Class A fire rating for safety."
        ]}
      />

      <FaqSection calculatorId="roofing" />
      <RelatedCalculators calculatorId="roofing" />
    </CalculatorShell>
  );
}
